import { useEffect, useState } from 'react';
import { EscalatorChapter } from './type';
import {
  formatDurationMs,
  getActiveChapterFromSchedule,
  getMsSinceMidnight,
  getNextChapterIndex,
} from './schedule';

interface EscalatorPreviewOverlayProps {
  chapters: EscalatorChapter[];
  pinnedChapterIndex: number | null;
}

export function EscalatorPreviewOverlay({
  chapters,
  pinnedChapterIndex,
}: EscalatorPreviewOverlayProps) {
  const [now, setNow] = useState(Date.now());
  const isPinned = pinnedChapterIndex !== null && !!chapters[pinnedChapterIndex];

  useEffect(() => {
    if (isPinned) {
      return;
    }

    const intervalId = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(intervalId);
  }, [isPinned]);

  if (!chapters.length) {
    return null;
  }

  const activeState = isPinned
    ? {
        chapterIndex: pinnedChapterIndex,
        chapter: chapters[pinnedChapterIndex],
        elapsedInChapterMs: 0,
      }
    : getActiveChapterFromSchedule(chapters, getMsSinceMidnight(now));

  if (!activeState) {
    return null;
  }

  const { chapterIndex, chapter, elapsedInChapterMs } = activeState;
  const chapterDuration = chapter.duration ?? 0;
  const remainingMs = isPinned
    ? chapterDuration
    : Math.max(0, chapterDuration - elapsedInChapterMs);
  const nextIndex = getNextChapterIndex(chapters, chapterIndex);
  const nextChapter = chapters[nextIndex];

  return (
    <div className="escalator-preview-overlay">
      <div className="escalator-preview-overlay__chapter">
        Chapter {chapterIndex + 1}
        <span className="escalator-preview-overlay__type"> ({chapter.chapter_type})</span>
        {isPinned ? <span className="escalator-preview-overlay__pinned"> · pinned</span> : null}
      </div>
      <div className="escalator-preview-overlay__next">
        Next: chapter {nextIndex + 1} ({nextChapter.chapter_type})
        {isPinned ? null : <> in {formatDurationMs(remainingMs)}</>}
      </div>
    </div>
  );
}
